import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { inside, interiorPages } from '../data/content';
export function InsidePreview() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = interiorPages.length;
  const updateActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slides = Array.from(track.children) as HTMLElement[];
    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    let minDistance = Infinity;
    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < minDistance) {
        minDistance = distance;
        closest = index;
      }
    });
    setActive(closest);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', updateActive, {
      passive: true
    });
    return () => track.removeEventListener('scroll', updateActive);
  }, [updateActive]);
  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const target = (index + total) % total;
    const slide = track.children[target] as HTMLElement | undefined;
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  };
  return <section className="bg-ivory text-ink" data-id="element-105" data-mp="2t25tkiuojmuz">
      <div className="mx-auto max-w-content py-12 lg:py-20" data-id="element-106" data-mp="2xzxnfievxj1t">
        <div className="px-5 text-center sm:px-6" data-id="element-107" data-mp="2y0h3suovzzdq">
          <h2 className="font-display text-[2rem] font-semibold uppercase leading-tight tracking-tight sm:text-5xl" data-id="element-108" data-mp="r9r7d7l3rw8r">
            {inside.title}
          </h2>
          <p className="mx-auto mt-3 max-w-md font-sans text-base text-ink/75 sm:text-lg" data-id="element-109" data-mp="w7svw81asnrm">{inside.subtitle}</p>
        </div>

        <div className="relative mt-8" role="region" aria-roledescription="carrusel" aria-label="Páginas interiores de Lo que nadie ve" data-id="element-110" data-mp="psc0s0knnkia">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[11%] pb-6 pt-2 [scrollbar-width:none] sm:gap-6 sm:px-[28%] lg:px-[34%] [&::-webkit-scrollbar]:hidden" data-id="element-111" data-mp="kv2cwpcwzgwk">
            {interiorPages.map((page, index) => <figure key={page.image} className="w-[78%] flex-none snap-center sm:w-[44%] lg:w-[32%]" aria-label={`Página ${index + 1} de ${total}`} data-id="element-112" data-mp="18l6580knju89" data-mpi={index}>
                <img src={page.image} alt={`${page.alt} (${index + 1} de ${total})`} loading={index < 2 ? 'eager' : 'lazy'} className={`block aspect-[707/1000] w-full rounded-lg bg-ivory-card object-cover object-top shadow-[0_20px_36px_-18px_rgba(15,20,28,0.55)] ring-1 ring-stone transition-[transform,opacity] duration-200 ease-out ${index === active ? 'scale-100 opacity-100' : 'scale-[0.94] opacity-60'}`} data-id="element-113" data-mp="1w8ecgo1n5dcx" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} aria-label="Página anterior" className="absolute left-2 top-[45%] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-ivory shadow-lg transition-colors duration-150 ease-out hover:bg-ink-raise focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:left-6" data-id="element-114" data-mp="2ip65x6bvr133">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-115" data-mp="31h8ncjwcilee" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} aria-label="Página siguiente" className="absolute right-2 top-[45%] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-ivory shadow-lg transition-colors duration-150 ease-out hover:bg-ink-raise focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink sm:right-6" data-id="element-116" data-mp="36fk1h6tobi2f">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-117" data-mp="3k6rp32m0kgdo" />
          </button>
        </div>

        <div className="mt-2 flex items-center justify-center gap-2" aria-live="polite" data-id="element-118" data-mp="3f8zxk9pa6wa0">
          {interiorPages.map((page, index) => <button key={page.image} type="button" onClick={() => goTo(index)} aria-label={`Ver página ${index + 1}`} aria-current={index === active} className={`h-2.5 rounded-full transition-[width,background-color] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${index === active ? 'w-7 bg-terra-deep' : 'w-2.5 bg-stone'}`} data-id="element-119" data-mp="3t9ap7eyf0ew8" data-mpi={index} />)}
        </div>

        <ul className="mx-auto mt-6 flex max-w-3xl flex-wrap justify-center gap-x-2 gap-y-1.5 px-5 text-center font-sans text-[0.85rem] font-semibold text-ink/80 sm:text-[0.95rem]" data-id="element-120" data-mp="122a427vlnc3b">
          {inside.features.map((feature, index) => <li key={feature} className="flex items-center gap-2" data-id="element-121" data-mp="1uqxs2b7qgfkk" data-mpi={index}>
              {index > 0 && <span aria-hidden="true" className="text-terra-deep" data-id="element-122" data-mp="1znxpjxqioa02">·</span>}
              {feature}
            </li>)}
        </ul>
      </div>
    </section>;
}